import { useMutation, useQueryClient } from '@tanstack/react-query';

import { sendErrorEvent } from '@Analytics';
import { AgentConversationInteractionsResponse, FeedbackReason, FeedbackType, UpdateFeedbackPayload } from '@Models';
import { bopsApi } from '@Network';
import { INTERACTION_LIMIT } from './useAgentInteractions';

interface UpdateFeedbackParams {
  conversationId: string;
  timestamp: string;
  feedback: FeedbackType;
  feedbackReason?: FeedbackReason | null;
  feedbackComment?: string | null;
  /** Must match the `limit` used by useAgentInteractions for this conversation. */
  limit?: number;
}

/** Single source for what the server and the optimistic cache both store. */
const normalizeComment = (comment?: string | null): string | null => comment?.trim() || null;

const toPayload = (params: UpdateFeedbackParams): UpdateFeedbackPayload => {
  const payload: UpdateFeedbackPayload = { timestamp: params.timestamp, feedback: params.feedback };
  const comment = normalizeComment(params.feedbackComment);
  if (params.feedbackReason) payload.feedback_reason = params.feedbackReason;
  if (comment) payload.feedback_comment = comment;
  return payload;
};

const queryKeyFor = (params: UpdateFeedbackParams) => [
  'agent-conversation-interactions',
  params.conversationId,
  params.limit ?? INTERACTION_LIMIT,
];

export const useFeedback = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params: UpdateFeedbackParams) =>
      bopsApi.updateInteractionFeedback(params.conversationId, toPayload(params)),
    onMutate: async (params) => {
      await queryClient.cancelQueries({
        queryKey: ['agent-conversation-interactions', params.conversationId],
      });

      const key = queryKeyFor(params);
      const previousData = queryClient.getQueryData<AgentConversationInteractionsResponse>(key);
      const isVote = params.feedback !== FeedbackType.NEUTRAL;

      queryClient.setQueryData<AgentConversationInteractionsResponse>(key, (old) => {
        if (!old) return old;
        return {
          ...old,
          interactions: old.interactions.map((interaction) =>
            interaction.timestamp === params.timestamp
              ? {
                  ...interaction,
                  feedback: params.feedback,
                  feedback_reason: isVote ? params.feedbackReason ?? null : null,
                  feedback_comment: isVote ? normalizeComment(params.feedbackComment) : null,
                }
              : interaction,
          ),
        };
      });

      return { previousData };
    },
    onError: (error, params, context) => {
      const message = error instanceof Error ? error.message : String(error);
      sendErrorEvent(`Failed to update feedback for conversation ${params.conversationId}. Error: ${message}`);
      if (context?.previousData) queryClient.setQueryData(queryKeyFor(params), context.previousData);
      queryClient.invalidateQueries({
        queryKey: ['agent-conversation-interactions', params.conversationId],
      });
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: ['agent-conversation-interactions', params.conversationId],
      });
    },
  });

  return {
    updateFeedback: mutation.mutate,
    isUpdating: mutation.isPending,
  };
};

export default useFeedback;
